/**
 * Utility functions for working with post types
 * Used across Newsfeed, PostItem, UseNewsfeed store, and newsFeedApi
 */

/**
 * Get the ID of a post based on its type
 * @param {Object} item - Post item object
 * @returns {number|null} Post ID or null if not found
 */
export const getPostId = (item) => {
	if (!item) {
		return null;
	}
	if (item.GlowID) {
		return item.GlowID;
	}
	if (item.GratitudeID) {
		return item.GratitudeID;
	}
	if (item.PrideID) {
		return item.PrideID;
	}
	return null;
};

/**
 * Detect the type of a post (glow, grateful, or pride)
 * @param {Object} item - Post item object
 * @returns {string} Post type ('glow', 'grateful', or 'pride')
 */
export const detectPostType = (item) => {
	if (!item) {
		return "glow";
	}

	// PRIORITIZE Type field over ID fields to avoid issues with incorrectly set ID fields
	// Check Type field first if it exists
	// Note: "Compliment" from API means a glow (giving someone a compliment), NOT grateful
	if (item.Type) {
		const normalizedType =
			typeof item.Type === "string" ? item.Type.toLowerCase() : item.Type;
		if (normalizedType === "grateful" || normalizedType === "gratitude") {
			return "grateful";
		}
		if (normalizedType === "pride" || normalizedType === "proud") {
			return "pride";
		}
		if (normalizedType === "glow" || normalizedType === "compliment") {
			// "Compliment" is a glow action (giving a compliment to someone)
			return "glow";
		}
	}

	// Fallback to ID fields only if Type field is not present or doesn't match known types
	// Check ID fields (only trust if they're truthy values, not 0 or empty)
	if (item.GratitudeID && item.GratitudeID !== 0) {
		return "grateful";
	}
	if (item.PrideID && item.PrideID !== 0) {
		return "pride";
	}

	// Default to glow
	return "glow";
};

/**
 * Get border color class for post type
 * @param {string} typeId - Post type ID ('glow', 'pride', 'grateful')
 * @returns {string} Border color class
 */
export const getPostTypeBorderColor = (typeId) => {
	if (typeId === "grateful") {
		return "border-green-500";
	}
	if (typeId === "pride") {
		return "border-purple-500";
	}
	if (typeId === "glow") {
		return "border-yellow-500";
	}
	return "border-gray-300";
};

/**
 * Get post type string for API calls
 * @param {Object} item - Post item object
 * @returns {string} Post type string
 */
export const getPostTypeString = (item) => {
	const type = detectPostType(item);
	return type === "grateful" ? "gratitude" : type;
};

/**
 * Get unique ID for a post (combines type and ID)
 * @param {Object} item - Post item object
 * @returns {string} Unique post identifier
 */
export const getPostUniqueId = (item) => {
	const id = getPostId(item);
	const type = detectPostType(item);
	return `${type}-${id}`;
};

/**
 * Find a post by ID in an array
 * @param {Array} posts - Array of posts
 * @param {number} id - Post ID to find
 * @returns {Object|null} Found post or null
 */
export const findPostById = (posts, id) => {
	return posts.find((post) => getPostId(post) === id) || null;
};

/**
 * Get absolute URL from a relative or absolute URL
 * @param {string} url - Relative or absolute URL
 * @returns {string} Absolute URL
 */
export const getAbsoluteUrl = (url) => {
	if (!url) {
		return "";
	}
	// If already absolute, return as is
	if (url.startsWith("http://") || url.startsWith("https://")) {
		return url;
	}
	// If relative, construct absolute URL
	if (typeof window !== "undefined") {
		// Get base URL from current location
		const baseUrl = `${window.location.protocol}//${window.location.host}`;
		// Ensure URL starts with /
		const path = url.startsWith("/") ? url : `/${url}`;
		return `${baseUrl}${path}`;
	}
	// Fallback for SSR or when window is not available
	// Use environment-based base URL
	const mode = import.meta.env.MODE;
	if (mode === "production") {
		return `https://goodglows.com${url.startsWith("/") ? url : `/${url}`}`;
	} else if (mode === "staging" || mode === "developmentstaging") {
		return `https://test.goodglows.com${url.startsWith("/") ? url : `/${url}`}`;
	}
	return url;
};
